import React, { useEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import './Editor.css';

let registered = false;
function registerBlots() {
    if (registered) return;
    registered = true;

    const Block = Quill.import('blots/block');
    Block.tagName = 'DIV';
    Quill.register(Block, true);

    const Embed = Quill.import('blots/block/embed');

    class Signature extends Embed {
        static create(paramValue) {
            const node = super.create();
            node.innerHTML = paramValue;
            return node;
        }
        static value(node) {
            return node.innerHTML;
        }
    }
    Signature.blotName = 'signature';
    Signature.tagName = 'signature';
    Quill.register(Signature);

    const Color = Quill.import('attributors/style/color');
    Color.whitelist = [];
    Quill.register(Color, true);

    class Table extends Embed {
        static create(paramValue) {
            return paramValue;
        }
        static value(node) {
            return node;
        }
    }
    Table.blotName = 'table';
    Table.tagName = 'table';
    Quill.register(Table, true);
}

/**
 * @deprecated Use FormComponents/Editor instead.
 */
export function FormEditor({ label, value, onChange, readOnly }) {
    return <div className="form-group flexclass">
        {label && <label className="col-xs-4 control-label">{label}</label>}
        <div className={`col-xs-${label ? 8 : 12}`}>
            <Editor value={value} onChange={onChange} readOnly={readOnly} />
        </div>
    </div>
}

/**
 * @deprecated Use FormComponents/Editor instead.
 */
export default function Editor({ value, onChange, readOnly }) {
    const containerRef = useRef(null);
    const quillRef = useRef(null);
    const lastEmittedRef = useRef('');
    const onChangeRef = useRef(onChange);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        registerBlots();

        const container = containerRef.current;
        const editorNode = document.createElement('div');
        container.appendChild(editorNode);

        const quill = new Quill(editorNode, {
            theme: 'snow',
            readOnly,
            modules: {
                toolbar: true,
                clipboard: { matchVisual: false },
            },
        });
        quillRef.current = quill;

        if (value) {
            quill.clipboard.dangerouslyPasteHTML(value);
            lastEmittedRef.current = quill.root.innerHTML;
        }

        const handleTextChange = (delta, oldDelta, source) => {
            if (source !== 'user') {
                return;
            }
            const html = quill.root.innerHTML;
            lastEmittedRef.current = html;
            if (onChangeRef.current) {
                onChangeRef.current(html);
            }
        };
        quill.on('text-change', handleTextChange);

        return () => {
            quill.off('text-change', handleTextChange);
            container.innerHTML = '';
            quillRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const quill = quillRef.current;
        if (!quill) return;
        if (value !== lastEmittedRef.current) {
            const sel = quill.getSelection();
            const delta = quill.clipboard.convert({ html: value || '' });
            quill.setContents(delta, 'silent');
            lastEmittedRef.current = quill.root.innerHTML;
            if (sel) {
                quill.setSelection(sel.index, sel.length, 'silent');
            }
        }
    }, [value]);

    useEffect(() => {
        const quill = quillRef.current;
        if (!quill) return;
        quill.enable(!readOnly);
        const toolbar = quill.getModule('toolbar');
        if (toolbar && toolbar.container) {
            toolbar.container.style.display = readOnly ? 'none' : '';
        }
    }, [readOnly]);

    return <div className="edit-quill" ref={containerRef} />;
}
